import { useId, useMemo, useRef, useState, type ReactNode } from "react";
import { MAX_LEAD_INTAKE_ORIGINS, type LeadIntakeArrival, type LeadIntakeDoor, type LeadIntakeSettingsResponse } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import { useTab } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { Chip } from "../../design/components/Chip";
import { DataTable } from "../../design/components/DataTable";
import { Dialog } from "../../design/components/Dialog";
import { RadioGroup } from "../../design/components/RadioGroup";
import { Reason } from "../../design/components/Reason";
import { SaveBar } from "../../design/components/SaveBar";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { TextArea } from "../../design/components/TextArea";
import { TextField } from "../../design/components/TextField";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { formatNumericDateTime } from "../../i18n/format";
import { useT, type Translate } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import { ConfirmChangesDialog, draftSummary, LeaveDialog, useLeaveGuard, type Feedback } from "./draft-dialogs";
import { describeIntakeChanges, draftOf, originToAdd, saveBodyOf, savedOf, snippetOf, type IntakeEdits, type OriginProblem } from "./lead-intake-draft";
import { MetaBox } from "./lead-intake-meta-box";
import { leadIntakeRecentQuery, leadIntakeSettingsQuery, useCopyLeadIntakeAddress, useLeadIntakeTestEntry, useRotateLeadIntakeAddress, useSaveLeadIntakeSettings } from "./lead-intake-queries";
import "./lead-intake.css";

// Impostazioni → «Ingressi dei Lead» (piano dell'ingresso del 10 ottobre 2026, I2, Task 4; piano M6, Task 17; mock-up
// `regole.html?sezione=ingressi`; IMP-1, IMP-2, UX-6, UX-7). Tre riquadri: «Moduli di Meta», con «Collega la pagina Facebook» (piano
// del 12 ottobre 2026, I3, `lead-intake-meta-box.tsx`); «Moduli del sito e delle landing», con l'indirizzo, «Copia l'indirizzo»,
// «Cambia l'indirizzo», i siti da cui arrivano i moduli e il pezzo di codice da mettere nella pagina; «GoHighLevel», con l'indirizzo, i
// passi da fare una volta sola e «Chi diventa owner». Sotto, «Fai entrare un Lead di prova» (soltanto nelle aziende di prova) e «Gli ultimi Lead arrivati».
// Accendere, spegnere, i siti e «Chi diventa owner» sono una bozza con un salvataggio solo e «Cosa cambia per il team» (IMP-2), sul
// modello di «Assegnazione dei Lead»; una versione cambiata nel frattempo (`409 version_conflict`) rilegge le porte e lo dice.
// Copiare, cambiare l'indirizzo e il Lead di prova partono subito. La sezione la vede ogni persona attiva; la cambia chi ha
// `settings.lead_intake` (`canEdit` del server: di partenza il Super admin dell'azienda e quello di Gioia365), e soltanto lui vede gli
// ultimi arrivi, che portano i nomi dei Lead di tutta l'azienda. L'indirizzo intero si vede soltanto dopo «Copia l'indirizzo».

export function LeadIntakePage() {
  return (
    <SettingsLayout section="leadIntake">
      <LeadIntakeSection />
    </SettingsLayout>
  );
}

function LeadIntakeSection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const settings = useQuery(leadIntakeSettingsQuery(tab));
  const organization = access.context.organization.name;

  let body: ReactNode;
  if (settings.data) {
    body = <LeadIntakeEditor base={settings.data} />;
  } else if (settings.isError) {
    body = (
      <div className="g-intake__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, settings.error)}
        </Reason>
        <Button onClick={() => void settings.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else {
    body = <p className="g-intake__muted">{t("common.loading")}</p>;
  }

  return (
    <>
      <AboutBox title={t("leadIntakeSettings.about")}>
        <p>{t("leadIntakeSettings.aboutText")}</p>
        <p>{t("leadIntakeSettings.aboutSame")}</p>
        <p>{t("leadIntakeSettings.aboutSecret", { organization })}</p>
      </AboutBox>
      {settings.data && !settings.data.canEdit ? <Reason>{t("leadIntakeSettings.readOnly", { organization })}</Reason> : null}
      {body}
    </>
  );
}

/** Che cosa è successo a un'azione che parte subito, sotto il suo pulsante. */
type Status = { tone: "info" | "warning"; text: string };

function LeadIntakeEditor({ base }: { base: LeadIntakeSettingsResponse }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const save = useSaveLeadIntakeSettings(tab);
  const editable = base.canEdit;
  const [edits, setEdits] = useState<IntakeEdits>({});
  const [confirming, setConfirming] = useState<{ error: Feedback | null } | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Feedback | null>(null);
  const savingNow = useRef(false);

  const saved = useMemo(() => savedOf(base), [base]);
  const draft = useMemo(() => draftOf(saved, editable ? edits : {}), [saved, editable, edits]);
  const changes = describeIntakeChanges(saved, draft, t);
  const dirty = editable && changes.length > 0;
  const blocker = useLeaveGuard(dirty);
  const disabled = !editable || saving;

  function change(update: IntakeEdits) {
    if (!editable || savingNow.current) return;
    setEdits((current) => ({ web: { ...current.web, ...update.web }, ghl: { ...current.ghl, ...update.ghl } }));
  }

  function discard() {
    setEdits({});
    setNotice(null);
    toast.show(t("settings.draft.discarded"));
  }

  async function apply() {
    if (!editable || savingNow.current || changes.length === 0) return;
    savingNow.current = true;
    setSaving(true);
    setConfirming({ error: null });
    try {
      await save.mutateAsync(saveBodyOf(base, draft));
      setConfirming(null);
      setEdits({});
      setNotice(null);
      toast.show(t("leadIntakeSettings.saved"), "success");
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === "version_conflict") {
        // Le porte si rileggono: le modifiche restano sopra quelle nuove, da controllare.
        setConfirming(null);
        setNotice({ text: (tr) => tr("leadIntakeSettings.conflict") });
      } else {
        setConfirming({ error: { text: (tr) => messageForError(tr, error) } });
      }
    } finally {
      savingNow.current = false;
      setSaving(false);
    }
  }

  return (
    <>
      {notice ? (
        <Reason tone="warning" role="alert">
          {notice.text(t)}
        </Reason>
      ) : null}

      <MetaBox base={base} />
      <WebBox base={base} draft={draft.web} disabled={disabled} editable={editable} onChange={(web) => change({ web })} />
      <GhlBox base={base} draft={draft.ghl} disabled={disabled} editable={editable} onChange={(ghl) => change({ ghl })} />
      <TestEntryBox base={base} />
      <RecentBox enabled={editable} />

      {dirty ? (
        <SaveBar summary={draftSummary(t, changes.length)} onDiscard={discard} onSave={() => setConfirming({ error: null })} saveLabel={t("settings.draft.save")} saving={saving} />
      ) : null}
      {confirming && dirty ? <ConfirmChangesDialog changes={changes} error={confirming.error} saving={saving} onBack={() => setConfirming(null)} onApply={() => void apply()} /> : null}
      {blocker.status === "blocked" ? (
        <LeaveDialog
          onStay={() => blocker.reset()}
          onLeave={() => {
            setEdits({});
            blocker.proceed();
          }}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// I tre riquadri

/** L'indirizzo di una porta: nascosto, intero dopo «Copia l'indirizzo», con «Copia l'indirizzo» e «Cambia l'indirizzo». */
type DoorAddressProps = {
  door: LeadIntakeDoor;
  base: LeadIntakeSettingsResponse;
  editable: boolean;
  /** L'indirizzo intero, dopo «Copia l'indirizzo»; altrimenti si vede quello nascosto. */
  full: string | null;
  onCopy: () => void;
  /** Dopo «Cambia l'indirizzo»: l'indirizzo intero di prima non vale più. */
  onRotated: () => void;
  status: Status | null;
};

function DoorAddress({ door, base, editable, full, onCopy, onRotated, status }: DoorAddressProps) {
  const t = useT();
  const id = useId();
  const tab = useTab();
  const rotate = useRotateLeadIntakeAddress(tab);
  const [asking, setAsking] = useState<{ error: string | null } | null>(null);
  const [rotated, setRotated] = useState(false);
  const view = base[door];
  const label = t(door === "web" ? "leadIntakeSettings.webAddress" : "leadIntakeSettings.ghlAddress");

  async function confirmRotate() {
    try {
      await rotate.mutateAsync(door);
      setAsking(null);
      setRotated(true);
      onRotated();
    } catch (error) {
      // L'esito resta nella finestra (UX-7).
      setAsking({ error: messageForError(t, error) });
    }
  }

  if (!view.exists || view.address === null) {
    return (
      <div className="g-field">
        <p className="g-field__label">{label}</p>
        <p className="g-intake__muted">{t("leadIntakeSettings.addressNone")}</p>
      </div>
    );
  }
  const shown = full ?? view.address;
  return (
    <div className="g-intake__address">
      <TextField
        id={`${id}-address`}
        label={label}
        value={shown}
        readOnly
        hint={view.active ? t("leadIntakeSettings.addressHidden") : t("leadIntakeSettings.off")}
        onFocus={(event) => event.currentTarget.select()}
      />
      {editable ? (
        <div className="g-intake__actions">
          <Button
            onClick={() => {
              setRotated(false);
              onCopy();
            }}
          >
            {t("leadIntakeSettings.copy")}
          </Button>
          <Button onClick={() => setAsking({ error: null })}>{t("leadIntakeSettings.rotate")}</Button>
        </div>
      ) : null}
      {status && !rotated ? (
        <p className="g-intake__status" data-tone={status.tone} role="status">
          {status.text}
        </p>
      ) : null}
      {rotated ? (
        <p className="g-intake__status" role="status">
          {t("leadIntakeSettings.rotated")}
        </p>
      ) : null}
      <Dialog
        open={asking !== null}
        title={t("leadIntakeSettings.rotateTitle")}
        onClose={() => {
          if (!rotate.isPending) setAsking(null);
        }}
        footer={
          <>
            <Button onClick={() => setAsking(null)} disabled={rotate.isPending}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={() => void confirmRotate()} disabled={rotate.isPending} aria-busy={rotate.isPending ? "true" : undefined}>
              {t("leadIntakeSettings.rotateConfirm")}
            </Button>
          </>
        }
      >
        <p>{t(door === "web" ? "leadIntakeSettings.rotateTextWeb" : "leadIntakeSettings.rotateTextGhl")}</p>
        {asking?.error ? (
          <Reason tone="warning" role="alert">
            {asking.error}
          </Reason>
        ) : null}
      </Dialog>
    </div>
  );
}

/** «Copia l'indirizzo» (e «Copia il codice»): l'indirizzo intero dal server, poi negli appunti; se il browser non lascia, nel campo. */
function useCopyAddress(door: LeadIntakeDoor) {
  const t = useT();
  const tab = useTab();
  const copyAddress = useCopyLeadIntakeAddress(tab);
  const [full, setFull] = useState<string | null>(null);
  const [status, setStatus] = useState<{ what: "address" | "code"; status: Status } | null>(null);

  async function copy(what: "address" | "code") {
    let address: string;
    try {
      address = (await copyAddress.mutateAsync(door)).address;
    } catch (error) {
      setStatus({ what, status: { tone: "warning", text: messageForError(t, error) } });
      return;
    }
    setFull(address);
    try {
      await navigator.clipboard.writeText(what === "code" ? snippetOf(address, t) : address);
      const copied: MessageKey = what === "code" ? "leadIntakeSettings.snippetCopied" : door === "web" ? "leadIntakeSettings.copiedWeb" : "leadIntakeSettings.copiedGhl";
      setStatus({ what, status: { tone: "info", text: t(copied) } });
    } catch {
      // Appunti negati: l'indirizzo intero resta nel campo (e nel codice), da copiare a mano.
      setStatus({ what, status: { tone: "warning", text: t("leadIntakeSettings.copyFailed") } });
    }
  }

  function forget() {
    setFull(null);
    setStatus(null);
  }

  return { full, copy, status, forget };
}

type WebDraft = { active: boolean; allowedOrigins: string[] };

function WebBox({ base, draft, disabled, editable, onChange }: { base: LeadIntakeSettingsResponse; draft: WebDraft; disabled: boolean; editable: boolean; onChange: (web: Partial<WebDraft>) => void }) {
  const t = useT();
  const id = useId();
  const copy = useCopyAddress("web");
  const [origin, setOrigin] = useState("");
  const [problem, setProblem] = useState<OriginProblem | null>(null);
  const problems: Record<OriginProblem, string> = {
    invalid: t("leadIntakeSettings.originInvalid"),
    taken: t("leadIntakeSettings.originTaken"),
    max: t("leadIntakeSettings.originsMax", { count: MAX_LEAD_INTAKE_ORIGINS }),
  };

  function add() {
    const result = originToAdd(origin, draft.allowedOrigins);
    if (!result.ok) {
      setProblem(result.problem);
      return;
    }
    setProblem(null);
    setOrigin("");
    onChange({ allowedOrigins: [...draft.allowedOrigins, result.origin] });
  }

  return (
    <section className="g-intake__box" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{t("leadIntakeSettings.web")}</h2>
      <Checkbox id={`${id}-active`} label={t("leadIntakeSettings.webActive")} checked={draft.active} disabled={disabled} onChange={(active) => onChange({ active })} />
      <DoorAddress
        door="web"
        base={base}
        editable={editable}
        full={copy.full}
        onCopy={() => void copy.copy("address")}
        onRotated={copy.forget}
        status={copy.status?.what === "address" ? copy.status.status : null}
      />
      <p className="g-intake__muted">{t("leadIntakeSettings.webOwner")}</p>

      <div className="g-intake__origins">
        <h3 id={`${id}-origins`}>{t("leadIntakeSettings.origins")}</h3>
        <p className="g-intake__muted">{t("leadIntakeSettings.originsHint")}</p>
        {draft.allowedOrigins.length === 0 ? (
          <p className="g-intake__muted">{t("leadIntakeSettings.originsNone")}</p>
        ) : (
          <ul className="g-intake__origin-list" aria-labelledby={`${id}-origins`}>
            {draft.allowedOrigins.map((value) => (
              <li key={value}>
                <Chip>{value}</Chip>
                {editable ? (
                  <Button variant="ghost" disabled={disabled} onClick={() => onChange({ allowedOrigins: draft.allowedOrigins.filter((other) => other !== value) })}>
                    {t("leadIntakeSettings.originRemove", { origin: value })}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {editable ? (
          <div className="g-intake__origin-add">
            <TextField
              id={`${id}-origin`}
              label={t("leadIntakeSettings.originField")}
              value={origin}
              disabled={disabled}
              inputMode="url"
              autoComplete="off"
              error={problem ? problems[problem] : undefined}
              onChange={(event) => {
                setOrigin(event.target.value);
                setProblem(null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  add();
                }
              }}
            />
            <Button disabled={disabled || origin.trim() === ""} onClick={add}>
              {t("leadIntakeSettings.originAdd")}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="g-intake__snippet">
        <h3>{t("leadIntakeSettings.snippet")}</h3>
        <p className="g-intake__muted">{t("leadIntakeSettings.snippetText")}</p>
        <TextArea id={`${id}-snippet`} label={t("leadIntakeSettings.snippetLabel")} value={snippetOf(copy.full ?? t("leadIntakeSettings.snippetPlaceholder"), t)} readOnly rows={8} spellCheck={false} className="g-intake__code" />
        {editable && base.web.exists ? (
          <div className="g-intake__actions">
            <Button onClick={() => void copy.copy("code")}>{t("leadIntakeSettings.snippetCopy")}</Button>
          </div>
        ) : null}
        {copy.status?.what === "code" ? (
          <p className="g-intake__status" data-tone={copy.status.status.tone} role="status">
            {copy.status.status.text}
          </p>
        ) : null}
      </div>
    </section>
  );
}

type GhlDraft = { active: boolean; ownerFrom: "tag" | "rule" };

function GhlBox({ base, draft, disabled, editable, onChange }: { base: LeadIntakeSettingsResponse; draft: GhlDraft; disabled: boolean; editable: boolean; onChange: (ghl: Partial<GhlDraft>) => void }) {
  const t = useT();
  const id = useId();
  const copy = useCopyAddress("ghl");
  const owners: Array<{ value: GhlDraft["ownerFrom"]; label: string }> = [
    { value: "tag", label: t("leadIntakeSettings.ownerTag") },
    { value: "rule", label: t("leadIntakeSettings.ownerRule") },
  ];
  return (
    <section className="g-intake__box" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{t("leadIntakeSettings.ghl")}</h2>
      <Checkbox id={`${id}-active`} label={t("leadIntakeSettings.ghlActive")} checked={draft.active} disabled={disabled} onChange={(active) => onChange({ active })} />
      <DoorAddress door="ghl" base={base} editable={editable} full={copy.full} onCopy={() => void copy.copy("address")} onRotated={copy.forget} status={copy.status?.status ?? null} />
      <h3>{t("leadIntakeSettings.ghlSteps")}</h3>
      <ol className="g-intake__steps">
        <li>{t("leadIntakeSettings.ghlStep1")}</li>
        <li>{t("leadIntakeSettings.ghlStep2")}</li>
        <li>{t("leadIntakeSettings.ghlStep3")}</li>
        <li>{t("leadIntakeSettings.ghlStep4")}</li>
      </ol>
      <h3 id={`${id}-owner`}>{t("leadIntakeSettings.owner")}</h3>
      <RadioGroup
        name={`${id}-owner-from`}
        legend={t("leadIntakeSettings.ownerLegend")}
        hideLegend
        options={owners}
        value={draft.ownerFrom}
        disabled={disabled}
        onChange={(ownerFrom) => onChange({ ownerFrom })}
      />
      <p className="g-intake__muted">{t("leadIntakeSettings.ownerNote")}</p>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Il Lead di prova e gli ultimi arrivi

function TestEntryBox({ base }: { base: LeadIntakeSettingsResponse }) {
  const t = useT();
  const id = useId();
  const tab = useTab();
  const access = useAccess();
  const testEntry = useLeadIntakeTestEntry(tab);
  const [result, setResult] = useState<{ arrival: LeadIntakeArrival } | { error: string } | null>(null);
  const timeZone = access.context.settings.timeZone;

  async function run() {
    setResult(null);
    try {
      setResult(await testEntry.mutateAsync());
    } catch (error) {
      setResult({ error: messageForError(t, error) });
    }
  }

  return (
    <section className="g-intake__box" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{t("leadIntakeSettings.test")}</h2>
      <p className="g-intake__muted">{t("leadIntakeSettings.testText")}</p>
      {base.canEdit && !base.testEntry ? <Reason id={`${id}-why`}>{t("leadIntakeSettings.testOnlyDemo")}</Reason> : null}
      <div className="g-intake__actions">
        <Button
          variant="primary"
          disabled={!base.testEntry || testEntry.isPending}
          aria-busy={testEntry.isPending ? "true" : undefined}
          aria-describedby={base.canEdit && !base.testEntry ? `${id}-why` : undefined}
          onClick={() => void run()}
        >
          {t("leadIntakeSettings.test")}
        </Button>
      </div>
      {result && "arrival" in result ? (
        <div className="g-intake__status" role="status">
          <p>{t("leadIntakeSettings.testDone")}</p>
          <p>{arrivalLine(t, timeZone, result.arrival)}</p>
        </div>
      ) : null}
      {result && "error" in result ? (
        <p className="g-intake__status" data-tone="warning" role="alert">
          {result.error}
        </p>
      ) : null}
    </section>
  );
}

const PROVIDERS: Readonly<Record<LeadIntakeArrival["provider"], MessageKey>> = {
  web: "leadIntakeSettings.providers.web",
  ghl: "leadIntakeSettings.providers.ghl",
  meta: "leadIntakeSettings.providers.meta",
};

const DISCARD_REASONS: Readonly<Record<string, MessageKey>> = {
  phone_missing: "leadIntakeSettings.discardReasons.phone_missing",
  invalid_phone: "leadIntakeSettings.discardReasons.invalid_phone",
  phone_invalid: "leadIntakeSettings.discardReasons.phone_invalid",
  archived_lead: "leadIntakeSettings.discardReasons.archived_lead",
  not_an_object: "leadIntakeSettings.discardReasons.not_an_object",
  honeypot: "leadIntakeSettings.discardReasons.honeypot",
  too_fast: "leadIntakeSettings.discardReasons.too_fast",
  not_owner: "leadIntakeSettings.discardReasons.not_owner",
};

const NOTES: Readonly<Record<string, MessageKey>> = {
  tag_unknown: "leadIntakeSettings.notes.tag_unknown",
  tag_ambiguous: "leadIntakeSettings.notes.tag_ambiguous",
  tag_inactive: "leadIntakeSettings.notes.tag_inactive",
  fill_time_missing: "leadIntakeSettings.notes.fill_time_missing",
  clock_ahead: "leadIntakeSettings.notes.clock_ahead",
};

const OWNER_VIAS: Readonly<Partial<Record<NonNullable<LeadIntakeArrival["ownerVia"]>, MessageKey>>> = {
  tag: "leadIntakeSettings.ownerVia.tag",
  rule: "leadIntakeSettings.ownerVia.rule",
  kept: "leadIntakeSettings.ownerVia.kept",
  chosen: "leadIntakeSettings.ownerVia.chosen",
};

/** «Cosa è successo», in parole: l'esito con il motivo, oppure perché l'arrivo è ancora da lavorare o non è entrato. */
export function arrivalText(t: Translate, arrival: LeadIntakeArrival): string {
  if (arrival.state === "failed") return t("leadIntakeSettings.results.failed", { attempts: arrival.attempts });
  if (arrival.state === "pending" || arrival.result === null) {
    return arrival.attempts > 0 ? t("leadIntakeSettings.results.retrying", { attempts: arrival.attempts }) : t("leadIntakeSettings.results.pending");
  }
  if (arrival.result === "discarded") {
    return t("leadIntakeSettings.results.discarded", { reason: t(DISCARD_REASONS[arrival.reason ?? ""] ?? "leadIntakeSettings.discardReasons.other") });
  }
  const what = t(`leadIntakeSettings.results.${arrival.result}`);
  const note = arrival.result === "duplicate" ? undefined : NOTES[arrival.reason ?? ""];
  return note === undefined ? what : t("leadIntakeSettings.withNote", { what, note: t(note) });
}

/** L'owner di un arrivo, con da dove viene: «Sara Conti (dal tag)», «Senza owner» per un Lead entrato senza. */
export function arrivalOwner(t: Translate, arrival: LeadIntakeArrival): string {
  if (arrival.owner !== null) {
    const via = arrival.ownerVia === null ? undefined : OWNER_VIAS[arrival.ownerVia];
    return via === undefined ? arrival.owner.displayName : t(via, { name: arrival.owner.displayName });
  }
  return arrival.result === "created" || arrival.result === "updated" ? t("leadIntakeSettings.noOwner") : t("leadIntakeSettings.none");
}

/** Una riga sola per l'esito del Lead di prova: il Lead, che cosa è successo, l'owner. */
function arrivalLine(t: Translate, timeZone: string, arrival: LeadIntakeArrival): string {
  return [formatNumericDateTime(timeZone, arrival.receivedAt), arrival.lead?.displayName ?? t("leadIntakeSettings.none"), arrivalText(t, arrival), arrivalOwner(t, arrival)].join(" · ");
}

function RecentBox({ enabled }: { enabled: boolean }) {
  const t = useT();
  const id = useId();
  const tab = useTab();
  const access = useAccess();
  const recent = useQuery(leadIntakeRecentQuery(tab, enabled));
  const timeZone = access.context.settings.timeZone;

  let body: ReactNode;
  if (!enabled) body = <p className="g-intake__muted">{t("leadIntakeSettings.recentOnlyEditors")}</p>;
  else if (recent.data && recent.data.arrivals.length === 0) body = <p className="g-intake__muted">{t("leadIntakeSettings.recentEmpty")}</p>;
  else if (recent.data) {
    body = (
      <DataTable caption={t("leadIntakeSettings.recentCaption")} captionHidden>
        <thead>
          <tr>
            <th scope="col">{t("leadIntakeSettings.columns.when")}</th>
            <th scope="col">{t("leadIntakeSettings.columns.from")}</th>
            <th scope="col">{t("leadIntakeSettings.columns.lead")}</th>
            <th scope="col">{t("leadIntakeSettings.columns.what")}</th>
            <th scope="col">{t("leadIntakeSettings.columns.owner")}</th>
          </tr>
        </thead>
        <tbody>
          {recent.data.arrivals.map((arrival) => (
            <tr key={arrival.id}>
              <th scope="row">{formatNumericDateTime(timeZone, arrival.receivedAt)}</th>
              <td>{t(PROVIDERS[arrival.provider])}</td>
              <td>
                {arrival.lead ? (
                  <Link to="/lead/$leadId" params={{ leadId: arrival.lead.id }}>
                    {arrival.lead.displayName ?? t("leadIntakeSettings.none")}
                  </Link>
                ) : (
                  t("leadIntakeSettings.none")
                )}
              </td>
              <td>{arrivalText(t, arrival)}</td>
              <td>{arrivalOwner(t, arrival)}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    );
  } else if (recent.isError) {
    body = (
      <Reason tone="warning" role="alert">
        {messageForError(t, recent.error)}
      </Reason>
    );
  } else body = <p className="g-intake__muted">{t("common.loading")}</p>;

  return (
    <section className="g-intake__box" aria-labelledby={`${id}-title`}>
      <div className="g-intake__heading">
        <h2 id={`${id}-title`}>{t("leadIntakeSettings.recent")}</h2>
        {enabled ? (
          <Button variant="ghost" onClick={() => void recent.refetch()} disabled={recent.isFetching} aria-busy={recent.isFetching ? "true" : undefined}>
            {t("leadIntakeSettings.refresh")}
          </Button>
        ) : null}
      </div>
      {body}
    </section>
  );
}
